import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { quickWebLogger } from '@quickweb/logger';

@Injectable({
  providedIn: 'root'
})
export class LogsService {

  public fluxo: string;
  public proposta: number;
  private startTime: number;
  private startAppTime: number;
  private currentPage: string;

  constructor() { }

  /**
   * Método privado para registrar logs com diferentes níveis de severidade.
   * @param level - Nível do log (info, error, warn, etc.).
   * @param message - Mensagem a ser registrada no log.
   * @param data - Dados adicionais a serem incluídos no log.
   */
  private log(level: string, message: string, data?: any): void {
    try {
      const logData = {
        fluxo: this.fluxo,
        proposta: this.proposta,
        ...data
      };

      switch (level) {
        case 'info':
          quickWebLogger.logger.info(message, logData);
          break;
        case 'error':
          quickWebLogger.logger.error(message, logData);
          break;
        case 'warn':
          quickWebLogger.logger.warn(message, logData);
          break;
        case 'alert':
          quickWebLogger.logger.alert(message, logData);
          break;
        case 'critical':
          quickWebLogger.logger.critical(message, logData);
          break;
        case 'notice':
          quickWebLogger.logger.notice(message, logData);
          break;
        case 'ok':
          quickWebLogger.logger.ok(message, logData);
          break;
        default:
          quickWebLogger.logger.info(message, logData);
      }
    } catch (error) {
      console.error('Erro ao registrar log:', error);
    }
  }

  /**
   * Registra um log de nível 'info'.
   * @param message - Mensagem a ser registrada.
   * @param data - Dados adicionais a serem incluídos no log.
   */
  public logInfo(message: string, data?: any): void {
    this.log('info', message, data);
  }

  /**
   * Registra um log de nível 'error'.
   * @param message - Mensagem a ser registrada.
   * @param data - Dados adicionais a serem incluídos no log.
   */
  public logError(message: string, data?: any): void {
    this.log('error', message, data);
  }

  /**
   * Registra um log de nível 'warn'.
   * @param message - Mensagem a ser registrada.
   * @param data - Dados adicionais a serem incluídos no log.
   */
  public logWarn(message: string, data?: any): void {
    this.log('warn', message, data);
  }

  /**
   * Registra um log de nível 'alert'.
   * @param message - Mensagem a ser registrada.
   * @param data - Dados adicionais a serem incluídos no log.
   */
  public logAlert(message: string, data?: any): void {
    this.log('alert', message, data);
  }

  /**
   * Registra um log de nível 'critical'.
   * @param message - Mensagem a ser registrada.
   * @param data - Dados adicionais a serem incluídos no log.
   */
  public logCritical(message: string, data?: any): void {
    this.log('critical', message, data);
  }

  /**
   * Registra um log de nível 'notice'.
   * @param message - Mensagem a ser registrada.
   * @param data - Dados adicionais a serem incluídos no log.
   */
  public logNotice(message: string, data?: any): void {
    this.log('notice', message, data);
  }

  /**
   * Registra um log de nível 'ok'.
   * @param message - Mensagem a ser registrada.
   * @param data - Dados adicionais a serem incluídos no log.
   */
  public logOk(message: string, data?: any): void {
    this.log('ok', message, data);
  }

  /**
   * Inicia o rastreamento do tempo gasto em uma página específica.
   * @param pageName - Nome da página a ser rastreada.
   */
  public startTracking(pageName: string): void {
    try {
      this.currentPage = pageName;
      this.startTime = Date.now();
      this.logNotice(`Iniciou na página ${this.currentPage}`);
    } catch (error) {
      console.log("Erro ao iniciar contador", error);
    }
  }

  /**
   * Finaliza o rastreamento e calcula o tempo gasto na página atual.
   */
  public stopTracking(): void {
    try {
      const endTime = Date.now();
      const timeSpent = endTime - this.startTime;
      const formattedTimeSpent = this.formatTime(timeSpent);
      this.logNotice(`Tempo gasto de ${formattedTimeSpent} na página ${this.currentPage}`, {
        page: this.currentPage,
        time: formattedTimeSpent
      });
    } catch (error) {
      console.log("Erro ao finalizar contador", error);
    }
  }

  /**
   * Registra um log quando a aplicação é iniciada.
   */
  public logAppStart(): void {
    const startTime = Date.now();
    this.startAppTime = startTime;
    this.logNotice('Iniciou a jornada', { time: startTime, evento: 'appStart' });
  }

  /**
   * Registra um log quando a aplicação é finalizada.
   */
  public logAppEnd(): void {
    const endTime = Date.now();
    const timeSpent = endTime - this.startAppTime;
    const formattedTimeSpent = this.formatTime(timeSpent);
    this.logNotice('Jornada finalizada', { tempo: endTime, evento: 'appEnd', tempoGasto: formattedTimeSpent });
  }

  /**
   * Formata o tempo em milissegundos para o formato hh:mm:ss.
   * @param milliseconds - Tempo em milissegundos a ser formatado.
   * @returns O tempo formatado como uma string no formato hh:mm:ss.
   */
  private formatTime(milliseconds: number): string {
    const seconds = Math.floor((milliseconds / 1000) % 60);
    const minutes = Math.floor((milliseconds / (1000 * 60)) % 60);
    const hours = Math.floor((milliseconds / (1000 * 60 * 60)) % 24);

    const formattedHours = hours.toString().padStart(2, '0');
    const formattedMinutes = minutes.toString().padStart(2, '0');
    const formattedSeconds = seconds.toString().padStart(2, '0');

    return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
  }

  /**
   * Registra um log quando o formulário possui campos inválidos.
   * @param formGroup - O grupo de formulários a ser verificado.
   * @param formulario - Nome do formulário.
   */
  public logInvalidFields(formGroup: FormGroup, formulario: string): void {
    const invalidFields = this.getInvalidFields(formGroup);
    this.logInfo(`Tentativa de avançar mas falhou ao preencher os dados ${formulario}`, { listaCamposInvalidos: invalidFields });
  }

  /**
   * Retorna uma lista de campos inválidos em um formulário.
   * @param form - O grupo de formulários a ser verificado.
   * @returns Uma lista de strings com os nomes dos campos inválidos.
   */
  private getInvalidFields(form: FormGroup): string[] {
    try {
      const invalidFields: string[] = [];
      Object.keys(form.controls).forEach(key => {
        const control = form.get(key);
        if (control instanceof FormGroup) {
          invalidFields.push(...this.getInvalidFields(control));
        } else if (control && control.invalid) {
          invalidFields.push(key);
        }
      });
      return invalidFields;
    } catch (error) {
      console.error('Erro ao buscar campos inválidos:', error);
      return ['erroListarDados'];
    }
  }
}